import { useQuery } from '@tanstack/react-query'
import { api, query, queryClient, type ApiResponse } from 'config/api'
import type { AppealDecisionPayload } from './appealDecision'
import { refreshPendingAppealsCount } from './usePendingAppealsCount'
import { refreshGradeAppealsList } from './useSectionAppeals'

type InstructorAppeal = ApiResponse<'api.v1.grade_appeals.show'>
export type InstructorAppealIdentity = {
  sectionId: string
  appealId: string
}

type AppealDecisionErrorCode =
  | 'ADJUSTMENT_NOT_HIGHER'
  | 'ACCEPT_LOWERS_GRADE'
  | 'PROPOSED_TOTAL_UNAVAILABLE'

const APPEAL_DECISION_ERROR_CODES: ReadonlySet<string> = new Set([
  'ADJUSTMENT_NOT_HIGHER',
  'ACCEPT_LOWERS_GRADE',
  'PROPOSED_TOTAL_UNAVAILABLE',
])

export function getAppealDecisionErrorCode(
  response: unknown
): AppealDecisionErrorCode | null {
  if (
    !response ||
    typeof response !== 'object' ||
    !('errorCode' in response) ||
    typeof response.errorCode !== 'string' ||
    !APPEAL_DECISION_ERROR_CODES.has(response.errorCode)
  ) {
    return null
  }

  return response.errorCode as AppealDecisionErrorCode
}

export function useInstructorAppeal({
  sectionId,
  appealId,
}: InstructorAppealIdentity) {
  const result = useQuery({
    ...query.api.v1.gradeAppeals.show.queryOptions({
      params: { sectionId, gradeAppealId: appealId },
    }),
    enabled: Boolean(sectionId && appealId),
    staleTime: 30_000,
  })

  return {
    ...result,
    appeal: result.data as InstructorAppeal | undefined,
  }
}

export async function decideInstructorAppeal({
  sectionId,
  appealId,
  payload,
}: InstructorAppealIdentity & {
  payload: AppealDecisionPayload
}) {
  return api.v1.gradeAppeals
    .decision({ params: { sectionId, gradeAppealId: appealId }, body: payload })
    .safe()
}

export async function invalidateInstructorAppeals({
  sectionId,
  appealId,
}: InstructorAppealIdentity) {
  await Promise.all([
    queryClient.invalidateQueries(
      query.api.v1.gradeAppeals.show.queryFilter({
        params: { sectionId, gradeAppealId: appealId },
      })
    ),
    refreshInstructorAppeals(),
  ])
}

export async function refreshInstructorAppeals() {
  await Promise.all([refreshGradeAppealsList(), refreshPendingAppealsCount()])
}
